import axios from 'axios';
import * as Sentry from '@sentry/react';
import { safeLocalStorage } from './utils/safeStorage';

// Create an Axios instance
const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_URI,
  withCredentials: true,  // Important: to include cookies in requests
  timeout: 60000, // Abort hung requests after 60s instead of waiting forever with no error
});

axiosInstance.interceptors.request.use(
    (config) => {
      const token = safeLocalStorage.getItem('token');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor - Auto-refresh on 403
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
      const originalRequest = error.config;
      const isTokenError = error.response?.headers?.['x-token-error'];
      const is403or401 = error.response?.status === 403 || error.response?.status === 401;

      // No response at all means the request either timed out client-side or the
      // network dropped it - these previously failed silently with nothing in Sentry.
      // Report them as their own event so we can see timeouts/network drops end to end,
      // separately from whichever screen's own error handling (if any) also reports them.
      if (!error.response) {
          const isTimeout = error.code === 'ECONNABORTED' && /timeout/i.test(error.message || '');
          Sentry.captureException(error, {
              tags: {
                  error_type: isTimeout ? 'request_timeout' : 'network_error',
                  request_method: originalRequest?.method,
                  request_url: originalRequest?.url,
              },
              extra: {
                  timeoutMs: originalRequest?.timeout,
                  baseURL: originalRequest?.baseURL,
              },
          });
      }

      // If token expired/invalid and we haven't already tried to refresh
      if (is403or401 && isTokenError && !originalRequest._retry) { // Don't retry refresh endpoint itself
          
          originalRequest._retry = true;

          try {
              console.log('Token expired, attempting refresh...');
              const refreshResponse = await axiosInstance.post('/api/user/refresh-token');
              
              if (refreshResponse.data.success) {
                  const newToken = refreshResponse.data.data.token;
                  safeLocalStorage.setItem('token', newToken);
                  console.log('Token refreshed successfully');
                  
                  // Update the failed request with new token and retry
                  originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                  return axiosInstance(originalRequest);
              }
          } catch (refreshError) {
              // Refresh failed - user truly needs to login
              console.error('Token refresh failed:', refreshError);
              safeLocalStorage.removeItem('token');
              
              // Only redirect if not already on login page
              if (window.location.pathname !== '/login') {
                  window.location.href = '/login';
              }
              return Promise.reject(refreshError);
          }
      }

      return Promise.reject(error);
  }
);

export default axiosInstance;